// src/pages/VisionBoardPage.js
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import html2canvas from "html2canvas";
import {
  PlusSquare,
  Type,
  Save,
  Trash2,
  Download,
  ChevronDown,
  X,
  Edit3,
  CheckCircle,
  AlertCircle,
  RotateCcw,
  Palette,
  CornerLeftUp,
  TextCursorInput,
  PaletteIcon,
  Baseline,
  Square,
  Layers,
  Droplet,
  Search,
  Cloud,
  RefreshCw,
  FilePlus,
  Loader,
  Edit,
} from "lucide-react";
import { db } from "../firebaseConfig";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
} from "firebase/firestore";

const FONT_FAMILIES = [
  "Arial, sans-serif",
  "Verdana, sans-serif",
  "Tahoma, sans-serif",
  "Georgia, serif",
  "Times New Roman, Times, serif",
  "Courier New, Courier, monospace",
  "Lucida Console, Monaco, monospace",
  "Comic Sans MS, cursive, sans-serif",
  "Impact, Charcoal, sans-serif",
  "Brush Script MT, Brush Script Std, cursive",
];

const PIXABAY_API_KEY = "50518525-906253bad8d79ea55dce1f4e9"; // Replace if needed

const EXPORT_SIZES = {
  "1920x1080": { width: 1920, height: 1080, name: "HD (16:9)" },
  "1080x1920": { width: 1080, height: 1920, name: "Story (9:16)" },
  "1080x1080": { width: 1080, height: 1080, name: "Square (1:1)" },
  "1280x720": { width: 1280, height: 720, name: "SD (16:9)" },
  Source: { width: 0, height: 0, name: "Original Size (Max 1920)" },
};

const VisionBoardPage = ({ user }) => {
  const [boardTitle, setBoardTitle] = useState("");
  const [items, setItems] = useState([]);
  const [nextItemId, setNextItemId] = useState(1);

  const [showBgSettings, setShowBgSettings] = useState(false);
  const [boardBgColor, setBoardBgColor] = useState("#FFFFFF");
  const [showGrid, setShowGrid] = useState(true);
  const [gridColor, setGridColor] = useState("rgba(200, 200, 200, 0.3)");
  const [gridSize, setGridSize] = useState(20);

  const [showTip, setShowTip] = useState(true);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [draggingItem, setDraggingItem] = useState(null);
  const [resizingItem, setResizingItem] = useState(null);
  const [editingTextItemId, setEditingTextItemId] = useState(null);
  const [editedText, setEditedText] = useState("");

  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState({
    show: false,
    boardId: null,
    boardTitle: "",
  });

  const [showImageSearch, setShowImageSearch] = useState(false);
  const [imageSearchQuery, setImageSearchQuery] = useState("");
  const [imageSearchResults, setImageSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const [boardsList, setBoardsList] = useState([]);
  const [currentBoardId, setCurrentBoardId] = useState(null);
  const [isLoadingBoards, setIsLoadingBoards] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [exportSize, setExportSize] = useState("1920x1080");
  const [showEditPanel, setShowEditPanel] = useState(true);

  const imageInputRef = useRef(null);
  const boardRef = useRef(null);

  const showToastMessage = useCallback(
    (message, type = "success", duration = 3000) => {
      setToast({ show: true, message, type });
      setTimeout(() => {
        setToast({ show: false, message: "", type: "success" });
      }, duration);
    },
    []
  );

  const updateItem = useCallback((id, updates) => {
    setItems((prevItems) =>
      prevItems.map((item) => (item.id === id ? { ...item, ...updates } : item))
    );
  }, []);

  const resetBoardState = useCallback((title = "") => {
    setBoardTitle(title);
    setItems([]);
    setBoardBgColor("#FFFFFF");
    setShowGrid(true);
    setGridColor("rgba(200, 200, 200, 0.3)");
    setGridSize(20);
    setCurrentBoardId(null);
    setSelectedItemId(null);
    setNextItemId(1);
  }, []);

  const loadBoardsFromFirestore = useCallback(async () => {
    if (!user) return;
    setIsLoadingBoards(true);
    try {
      const q = query(
        collection(db, "visionBoards"),
        where("userId", "==", user.uid)
      );
      const querySnapshot = await getDocs(q);
      const loadedBoards = [];
      querySnapshot.forEach((doc) => {
        loadedBoards.push({ id: doc.id, ...doc.data() });
      });
      setBoardsList(
        loadedBoards.sort(
          (a, b) => (b.updatedAt?.toDate() || 0) - (a.updatedAt?.toDate() || 0)
        )
      );
    } catch (error) {
      console.error("Error loading boards:", error);
      showToastMessage(`Failed to load boards: ${error.message}`, "error");
    } finally {
      setIsLoadingBoards(false);
    }
  }, [user, showToastMessage]);

  const handleNewBoard = useCallback(() => {
    resetBoardState();
    showToastMessage("Started a new board.", "info");
  }, [showToastMessage, resetBoardState]);

  const handleLoadBoard = useCallback(
    async (boardId) => {
      if (!boardId || isLoadingBoards) return;
      const boardToLoad = boardsList.find((b) => b.id === boardId);
      if (!boardToLoad || !boardToLoad.boardData) {
        showToastMessage("Could not find board data.", "error");
        return;
      }
      setIsLoadingBoards(true);
      try {
        const data = boardToLoad.boardData;
        setBoardTitle(data.title || "");
        const loadedItems = (data.items || []).map((item) => ({
          rotation: 0,
          ...(item.type === "image" && {
            borderWidth: 5,
            borderColor: "#FFFFFF",
            borderRadius: 0,
            aspectRatio: item.width / item.height || 1,
          }),
          ...(item.type === "text" && {
            fontFamily: FONT_FAMILIES[0],
            fontSize: 24,
            textColor: "#000000",
            textOutline: false,
            textOutlineColor: "#FFFFFF",
            textOutlineWidth: 1,
            textShadow: false,
          }),
          ...item,
        }));
        setItems(loadedItems);
        setBoardBgColor(data.bgColor || "#FFFFFF");
        if (data.grid) {
          setShowGrid(
            data.grid.showGrid !== undefined ? data.grid.showGrid : true
          );
          setGridColor(data.grid.color || "rgba(200, 200, 200, 0.3)");
          setGridSize(data.grid.size || 20);
        }
        let maxIdNum = 0;
        loadedItems.forEach((item) => {
          const numPart = String(item.id).split("-")[1];
          const num = parseInt(numPart);
          if (!isNaN(num) && num > maxIdNum) maxIdNum = num;
        });
        setNextItemId(maxIdNum + 1);
        setCurrentBoardId(boardId);
        setSelectedItemId(null);
        showToastMessage(
          `Board "${data.title || "Untitled"}" loaded!`,
          "success"
        );
      } catch (error) {
        console.error("Error loading board:", error);
        showToastMessage("Failed to load board.", "error");
      } finally {
        setIsLoadingBoards(false);
      }
    },
    [boardsList, showToastMessage, isLoadingBoards]
  );

  const handleSaveBoard = useCallback(async () => {
    if (!user) {
      showToastMessage("Must be signed in.", "error");
      return;
    }
    setIsSaving(true);
    const saveTitle = boardTitle.trim() || "Untitled Vision Board";
    const boardData = {
      title: saveTitle,
      items,
      bgColor: boardBgColor,
      grid: { showGrid, color: gridColor, size: gridSize },
    };
    try {
      if (currentBoardId) {
        const boardRef = doc(db, "visionBoards", currentBoardId);
        await setDoc(
          boardRef,
          {
            userId: user.uid,
            title: saveTitle,
            boardData,
            updatedAt: Timestamp.now(),
          },
          { merge: true }
        );
        showToastMessage(`Board updated!`, "success");
      } else {
        const docRef = await addDoc(collection(db, "visionBoards"), {
          userId: user.uid,
          title: saveTitle,
          boardData,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
        setCurrentBoardId(docRef.id);
        showToastMessage(`Board saved!`, "success");
      }
      setBoardTitle(saveTitle);
      loadBoardsFromFirestore();
    } catch (error) {
      console.error("Error saving board:", error);
      showToastMessage(`Save failed: ${error.message}`, "error");
    } finally {
      setIsSaving(false);
    }
  }, [
    user,
    boardTitle,
    items,
    boardBgColor,
    showGrid,
    gridColor,
    gridSize,
    currentBoardId,
    showToastMessage,
    loadBoardsFromFirestore,
  ]);

  const openDeleteConfirm = (boardId, title, e) => {
    e.stopPropagation();
    setShowDeleteConfirm({ show: true, boardId, boardTitle: title });
  };
  const closeDeleteConfirm = () =>
    setShowDeleteConfirm({ show: false, boardId: null, boardTitle: "" });
  const handleDeleteBoard = useCallback(async () => {
    const boardIdToDelete = showDeleteConfirm.boardId;
    if (!user || !boardIdToDelete) return;
    setIsDeleting(true);
    closeDeleteConfirm();
    try {
      await deleteDoc(doc(db, "visionBoards", boardIdToDelete));
      showToastMessage(`Board deleted.`, "success");
      setBoardsList((prev) => prev.filter((b) => b.id !== boardIdToDelete));
      if (currentBoardId === boardIdToDelete) {
        handleNewBoard();
      }
    } catch (error) {
      console.error("Error deleting board:", error);
      showToastMessage(`Delete failed: ${error.message}`, "error");
    } finally {
      setIsDeleting(false);
    }
  }, [
    user,
    showToastMessage,
    currentBoardId,
    showDeleteConfirm.boardId,
    handleNewBoard,
  ]);

  const addItem = useCallback(
    (
      type,
      content,
      initialWidth = 200,
      initialHeight = type === "image" ? 150 : 50,
      targetX,
      targetY
    ) => {
      const boardRect = boardRef.current?.getBoundingClientRect();
      const xPos =
        targetX !== undefined
          ? targetX
          : boardRect
            ? boardRect.width / 2 - initialWidth / 2
            : 50;
      const yPos =
        targetY !== undefined
          ? targetY
          : boardRect
            ? boardRect.height / 2 - initialHeight / 2
            : 50;
      const newItem = {
        id: `item-${nextItemId}`,
        type,
        content,
        x: xPos,
        y: yPos,
        width: initialWidth,
        height: initialHeight,
        rotation: 0,
        zIndex: items.length + 1,
        ...(type === "image" && {
          borderWidth: 5,
          borderColor: "#FFFFFF",
          borderRadius: 0,
          aspectRatio: initialWidth / initialHeight,
        }),
        ...(type === "text" && {
          fontFamily: FONT_FAMILIES[0],
          fontSize: 24,
          textColor: "#000000",
          textOutline: false,
          textOutlineColor: "#FFFFFF",
          textOutlineWidth: 1,
          textShadow: false,
        }),
      };
      setItems((prevItems) => [...prevItems, newItem]);
      setNextItemId((prevId) => prevId + 1);
      setSelectedItemId(newItem.id);
      if (type === "text") {
        setEditingTextItemId(newItem.id);
        setEditedText(content);
      }
    },
    [nextItemId, items.length]
  );

  const handleAddImageClick = () => imageInputRef.current.click();

  // --- MODIFIED: Added explicit semicolons ---
  const handleImageUpload = useCallback(
    (event) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;
      const boardRect = boardRef.current?.getBoundingClientRect();
      Array.from(files).forEach((file) => {
        if (file.size > 5 * 1024 * 1024) {
          showToastMessage(
            `Image "${file.name}" too large (max 5MB). Skipped.`,
            "error"
          );
          return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            const MAX_DIM = 300;
            let w = img.width;
            let h = img.height;
            if (w > MAX_DIM || h > MAX_DIM) {
              if (w / h > 1) {
                h = (MAX_DIM / w) * h;
                w = MAX_DIM;
              } else {
                w = (MAX_DIM / h) * w;
                h = MAX_DIM;
              }
            }
            let randomX = 50;
            let randomY = 50;
            if (boardRect) {
              randomX = Math.random() * Math.max(0, boardRect.width - w);
              randomY = Math.random() * Math.max(0, boardRect.height - h);
            }
            addItem("image", e.target.result, w, h, randomX, randomY);
          }; // Explicit semicolon
          img.src = e.target.result;
        }; // Explicit semicolon
        reader.readAsDataURL(file);
      });
      showToastMessage(
        `${files.length} image(s) selected. Processing...`,
        "success"
      );
      if (event.target) {
        // Defensive check
        event.target.value = null;
      }
    },
    [addItem, showToastMessage]
  );
  // --- END MODIFIED SECTION ---

  const handleAddText = useCallback(() => {
    const boardRect = boardRef.current?.getBoundingClientRect();
    const initialWidth = 200;
    const initialHeight = 60;
    const targetX = boardRect ? boardRect.width / 2 - initialWidth / 2 : 50;
    const targetY = boardRect ? boardRect.height / 2 - initialHeight / 2 : 50;
    addItem("text", "New Text", initialWidth, initialHeight, targetX, targetY);
    showToastMessage("Text added.", "success");
  }, [addItem, showToastMessage]);

  const handleItemMouseDown = useCallback(
    (id, e) => {
      e.stopPropagation();
      if (resizingItem) return;
      setSelectedItemId(id);
      const item = items.find((i) => i.id === id);
      if (!item) return;
      const maxZIndex = items.reduce(
        (max, i) => Math.max(max, i.zIndex || 0),
        0
      );
      if ((item.zIndex || 0) <= maxZIndex) {
        updateItem(id, { zIndex: maxZIndex + 1 });
      }
      const initialX = e.clientX;
      const initialY = e.clientY;
      setDraggingItem({ id, initialX, initialY, itemX: item.x, itemY: item.y });
    },
    [items, updateItem, resizingItem]
  );

  const handleResizeHandleMouseDown = useCallback(
    (itemId, handleType, e) => {
      e.stopPropagation();
      const item = items.find((i) => i.id === itemId);
      if (!item) return;
      setSelectedItemId(itemId);
      setResizingItem({
        id: itemId,
        initialX: e.clientX,
        initialY: e.clientY,
        initialWidth: item.width,
        initialHeight: item.height,
        itemX: item.x,
        itemY: item.y,
        aspectRatio:
          item.type === "image" ? item.aspectRatio : item.width / item.height,
        handle: handleType,
      });
    },
    [items]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (draggingItem && boardRef.current) {
        const boardRect = boardRef.current.getBoundingClientRect();
        let newX = draggingItem.itemX + (e.clientX - draggingItem.initialX);
        let newY = draggingItem.itemY + (e.clientY - draggingItem.initialY);
        const item = items.find((i) => i.id === draggingItem.id);
        if (item) {
          newX = Math.max(0, Math.min(newX, boardRect.width - item.width));
          newY = Math.max(0, Math.min(newY, boardRect.height - item.height));
        }
        updateItem(draggingItem.id, { x: newX, y: newY });
      } else if (resizingItem && boardRef.current) {
        const dx = e.clientX - resizingItem.initialX;
        const dy = e.clientY - resizingItem.initialY;
        let newWidth = resizingItem.initialWidth;
        let newHeight = resizingItem.initialHeight;
        if (resizingItem.handle === "br") {
          newWidth = resizingItem.initialWidth + dx;
          newHeight = resizingItem.initialHeight + dy;
        }
        const currentItem = items.find((i) => i.id === resizingItem.id);
        if (
          currentItem &&
          (currentItem.type === "image" ||
            (resizingItem.aspectRatio && resizingItem.handle === "br"))
        ) {
          if (Math.abs(dx) > Math.abs(dy)) {
            newHeight = newWidth / resizingItem.aspectRatio;
          } else {
            newWidth = newHeight * resizingItem.aspectRatio;
          }
        }
        newWidth = Math.max(20, newWidth);
        newHeight = Math.max(20, newHeight);
        updateItem(resizingItem.id, { width: newWidth, height: newHeight });
      }
    },
    [draggingItem, resizingItem, items, updateItem]
  );

  const handleMouseUp = useCallback(() => {
    setDraggingItem(null);
    setResizingItem(null);
  }, []);

  const handleTextDoubleClick = useCallback((item) => {
    if (item.type === "text") {
      setEditingTextItemId(item.id);
      setEditedText(item.content);
      setSelectedItemId(item.id);
    }
  }, []);

  const handleTextChange = (e) => setEditedText(e.target.value);
  const handleTextBlur = useCallback(() => {
    if (editingTextItemId) {
      updateItem(editingTextItemId, { content: editedText });
      setEditingTextItemId(null);
    }
  }, [editingTextItemId, editedText, updateItem]);
  const handleTextKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleTextBlur();
      } else if (e.key === "Escape") {
        setEditingTextItemId(null);
      }
    },
    [handleTextBlur]
  );
  const deleteItem = useCallback(
    (id) => {
      setItems((prevItems) => prevItems.filter((item) => item.id !== id));
      if (selectedItemId === id) setSelectedItemId(null);
      showToastMessage("Item removed.", "success");
    },
    [selectedItemId, showToastMessage]
  );

  const handleClearBoard = () => setShowClearConfirm(true);
  const confirmClearBoard = useCallback(() => {
    resetBoardState();
    showToastMessage("Board cleared.", "success");
    setShowClearConfirm(false);
  }, [showToastMessage, resetBoardState]);
  const cancelClearBoard = () => setShowClearConfirm(false);

  const handleExportBoard = useCallback(() => {
    const selectedDim = EXPORT_SIZES[exportSize];
    if (!selectedDim || !boardRef.current) {
      showToastMessage("Select size.", "error");
      return;
    }
    showToastMessage("Preparing export...", "info");
    const previouslySelected = selectedItemId;
    setSelectedItemId(null);
    setTimeout(() => {
      const sourceElement = boardRef.current;
      html2canvas(sourceElement, {
        backgroundColor: boardBgColor,
        width: sourceElement.scrollWidth,
        height: sourceElement.scrollHeight,
        scale: 2,
        logging: false,
        useCORS: true,
        onclone: (documentClone) => {
          const boardClone = documentClone.getElementById("vision-board-area");
          if (boardClone) {
            boardClone.style.boxShadow = "none";
            boardClone.style.border = "none";
          }
          documentClone
            .querySelectorAll(".item-controls-for-export")
            .forEach((el) => (el.style.display = "none"));
          documentClone
            .querySelectorAll(".resize-handle-for-export")
            .forEach((el) => (el.style.display = "none"));
        },
      })
        .then((sourceCanvas) => {
          let targetWidth, targetHeight;
          if (exportSize === "Source") {
            targetWidth = Math.min(sourceCanvas.width / 2, 1920);
            targetHeight =
              (targetWidth / sourceCanvas.width) * sourceCanvas.height;
          } else {
            targetWidth = selectedDim.width;
            targetHeight = selectedDim.height;
          }
          const finalCanvas = document.createElement("canvas");
          finalCanvas.width = targetWidth;
          finalCanvas.height = targetHeight;
          const ctx = finalCanvas.getContext("2d");
          ctx.fillStyle = boardBgColor;
          ctx.fillRect(0, 0, targetWidth, targetHeight);
          const scaleX = targetWidth / sourceCanvas.width;
          const scaleY = targetHeight / sourceCanvas.height;
          const scaleToFit = Math.min(scaleX, scaleY);
          const scaledWidth = sourceCanvas.width * scaleToFit;
          const scaledHeight = sourceCanvas.height * scaleToFit;
          const offsetX = (targetWidth - scaledWidth) / 2;
          const offsetY = (targetHeight - scaledHeight) / 2;
          ctx.drawImage(
            sourceCanvas,
            offsetX,
            offsetY,
            scaledWidth,
            scaledHeight
          );
          const image = finalCanvas.toDataURL("image/png");
          const link = document.createElement("a");
          link.download = `${(boardTitle || "vision-board").replace(
            /[^a-zA-Z0-9]/g,
            "_"
          )}.png`;
          link.href = image;
          link.click();
          showToastMessage("Board exported!", "success");
        })
        .catch((err) => {
          console.error("Error exporting board: ", err);
          showToastMessage("Export failed.", "error");
        })
        .finally(() => {
          setSelectedItemId(previouslySelected);
        });
    }, 150);
  }, [
    boardRef,
    boardBgColor,
    boardTitle,
    selectedItemId,
    showToastMessage,
    exportSize,
  ]);

  const handleBoardClick = useCallback(
    (e) => {
      if (
        boardRef.current &&
        boardRef.current.contains(e.target) &&
        e.target === boardRef.current
      ) {
        setSelectedItemId(null);
        if (editingTextItemId) handleTextBlur();
      }
    },
    [editingTextItemId, handleTextBlur]
  );

  const handleImageSearch = useCallback(async () => {
    if (!imageSearchQuery.trim()) {
      showToastMessage("Enter search.", "error");
      return;
    }
    if (PIXABAY_API_KEY === "YOUR_PIXABAY_API_KEY" || !PIXABAY_API_KEY) {
      showToastMessage("Pixabay API key not set.", "error");
      return;
    }
    setIsSearching(true);
    setImageSearchResults([]);
    try {
      const pixabayUrl = `https://pixabay.com/api/?key=${PIXABAY_API_KEY}&q=${encodeURIComponent(
        imageSearchQuery
      )}&image_type=photo&per_page=12`;
      const response = await fetch(pixabayUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data.hits && data.hits.length > 0) {
        const urls = data.hits.map((hit) => hit.webformatURL);
        setImageSearchResults(urls);
        showToastMessage(`Found ${urls.length} images.`, "success");
      } else {
        setImageSearchResults([]);
        showToastMessage(`No images found.`, "info");
      }
    } catch (error) {
      console.error("Image search error:", error);
      showToastMessage("Image search failed.", "error");
    } finally {
      setIsSearching(false);
    }
  }, [imageSearchQuery, showToastMessage]);
  const handleAddSearchedImage = useCallback(
    (imageUrl) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = () => {
        const MAX_DIM = 300;
        let w = img.width;
        let h = img.height;
        if (w > MAX_DIM || h > MAX_DIM) {
          if (w / h > 1) {
            h = (MAX_DIM / w) * h;
            w = MAX_DIM;
          } else {
            w = (MAX_DIM / h) * w;
            h = MAX_DIM;
          }
        }
        const boardRect = boardRef.current?.getBoundingClientRect();
        let randomX = boardRect ? boardRect.width / 2 - w / 2 : 50;
        let randomY = boardRect ? boardRect.height / 2 - h / 2 : 50;
        addItem("image", imageUrl, w, h, randomX, randomY);
        showToastMessage("Image added!", "success");
      };
      img.onerror = () => {
        showToastMessage("Failed to load image.", "error");
      };
      img.src = imageUrl;
    },
    [addItem, showToastMessage]
  );

  useEffect(() => {
    if (user) {
      loadBoardsFromFirestore();
    } else {
      setBoardsList([]);
      resetBoardState();
    }
  }, [user, loadBoardsFromFirestore, resetBoardState]);

  useEffect(() => {
    if (draggingItem || resizingItem) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [draggingItem, resizingItem, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (selectedItemId) {
      setShowEditPanel(true);
    }
  }, [selectedItemId]);

  const buttonBaseClass =
    "flex items-center justify-center space-x-2 font-semibold py-2.5 px-4 sm:px-5 rounded-lg transition duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-100 disabled:opacity-60 disabled:cursor-not-allowed text-sm";
  const boardStyle = {
    backgroundColor: boardBgColor,
    backgroundImage: showGrid
      ? `linear-gradient(to right, ${gridColor} 1px, transparent 1px), linear-gradient(to bottom, ${gridColor} 1px, transparent 1px)`
      : "none",
    backgroundSize: showGrid ? `${gridSize}px ${gridSize}px` : "auto",
  };
  const selectedItemData = useMemo(
    () => items.find((item) => item.id === selectedItemId),
    [items, selectedItemId]
  );

  const editLabelClass = "block text-xs font-medium text-slate-600 mb-1";
  const editInputClass =
    "w-full p-2 border border-slate-400 rounded-md shadow-sm focus:ring-sky-500 focus:border-sky-500 bg-white text-slate-700 placeholder-gray-400 text-sm";
  const editSelectClass = `${editInputClass} appearance-none`;
  const editRangeClass =
    "w-full h-2 bg-slate-300 rounded-lg appearance-none cursor-pointer accent-sky-500";

  return (
    <div className="flex-grow flex flex-col p-3 sm:p-6 bg-slate-800 text-white min-h-screen overflow-y-auto">
      {toast.show && (
        <div
          className={`fixed top-5 right-5 p-4 rounded-md shadow-lg text-white z-[100] ${toast.type === "success"
            ? "bg-green-500"
            : toast.type === "error"
              ? "bg-red-500"
              : "bg-sky-500"
            }`}
        >
          <div className="flex items-center">
            {toast.type === "success" ? (
              <CheckCircle className="mr-2" />
            ) : (
              <AlertCircle className="mr-2" />
            )}
            {toast.message}
          </div>
        </div>
      )}
      {showClearConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[90] p-4">
          <div className="bg-slate-100 p-5 sm:p-6 rounded-lg shadow-xl text-slate-800 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4 text-slate-700">
              Confirm Clear Board
            </h3>
            <p className="text-sm text-slate-600 mb-6">
              Are you sure you want to clear the entire board?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelClearBoard}
                className={`${buttonBaseClass} bg-slate-400 hover:bg-slate-500 text-white focus:ring-slate-300`}
              >
                Cancel
              </button>
              <button
                onClick={confirmClearBoard}
                className={`${buttonBaseClass} bg-red-500 hover:bg-red-600 text-white focus:ring-red-400`}
              >
                Clear Board
              </button>
            </div>
          </div>
        </div>
      )}
      {showDeleteConfirm.show && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[90] p-4">
          <div className="bg-slate-100 p-5 sm:p-6 rounded-lg shadow-xl text-slate-800 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4 text-slate-700">
              Confirm Delete Board
            </h3>
            <p className="text-sm text-slate-600 mb-6">
              Are you sure you want to permanently delete "
              {showDeleteConfirm.boardTitle}"?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={closeDeleteConfirm}
                disabled={isDeleting}
                className={`${buttonBaseClass} bg-slate-400 hover:bg-slate-500 text-white focus:ring-slate-300`}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteBoard}
                disabled={isDeleting}
                className={`${buttonBaseClass} bg-red-500 hover:bg-red-600 text-white focus:ring-red-400`}
              >
                {isDeleting ? (
                  <Loader className="animate-spin" size={18} />
                ) : (
                  "Delete Board"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <input
        type="text"
        value={boardTitle}
        onChange={(e) => setBoardTitle(e.target.value)}
        className="text-2xl sm:text-3xl font-bold text-sky-300 mb-4 text-center bg-transparent border-none focus:ring-0 focus:outline-none hover:bg-slate-700/50 rounded-md p-2 w-full max-w-xl mx-auto placeholder-slate-500"
        placeholder="Click here to name your board"
      />

      <div className="bg-slate-700 text-white p-3 sm:p-4 rounded-xl shadow-lg mb-4">
        <h3 className="text-lg font-semibold text-sky-300 mb-3 flex items-center">
          <Cloud size={20} className="mr-2" />
          Board Management
        </h3>
        <div className="flex flex-col sm:flex-row gap-3 items-center">
          <button
            onClick={handleNewBoard}
            disabled={!user}
            className={`${buttonBaseClass} bg-green-500 hover:bg-green-600 text-white focus:ring-green-400 w-full sm:w-auto`}
          >
            <FilePlus size={18} />
            <span>New Board</span>
          </button>
          <div className="flex-grow w-full">
            <label
              htmlFor="boardSelect"
              className="block text-xs font-medium text-gray-300 mb-1"
            >
              Load Saved Board:
            </label>
            <div className="flex items-center gap-2">
              <select
                id="boardSelect"
                value={currentBoardId || ""}
                onChange={(e) => handleLoadBoard(e.target.value)}
                disabled={!user || isLoadingBoards || boardsList.length === 0}
                className={`${editInputClass.replace(
                  "bg-white text-slate-700",
                  "bg-slate-600 text-white"
                )} flex-grow !placeholder-gray-400`}
              >
                <option value="" disabled>
                  {" "}
                  {!user
                    ? "Sign in to load boards"
                    : isLoadingBoards
                      ? "Loading..."
                      : boardsList.length === 0
                        ? "No saved boards"
                        : "Select a board to load..."}{" "}
                </option>
                {boardsList.map((board) => (
                  <option key={board.id} value={board.id}>
                    {" "}
                    {board.title || "Untitled Board"} (
                    {new Date(
                      board.updatedAt?.toDate() || Date.now()
                    ).toLocaleDateString()}
                    ){" "}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <button
            onClick={loadBoardsFromFirestore}
            disabled={!user || isLoadingBoards}
            className={`${buttonBaseClass} bg-blue-500 hover:bg-blue-600 text-white focus:ring-blue-400 w-full sm:w-auto`}
            title="Refresh Board List"
          >
            {isLoadingBoards ? (
              <Loader className="animate-spin" size={18} />
            ) : (
              <RefreshCw size={18} />
            )}
            <span>{isLoadingBoards ? "Refreshing..." : "Refresh"}</span>
          </button>
        </div>
        {user && boardsList.length > 0 && (
          <div className="mt-3 bg-slate-600 p-2 rounded-md max-h-40 overflow-y-auto">
            {" "}
            <p className="text-xs text-slate-300 mb-1">
              Click title to load, <Trash2 size={12} className="inline" /> to
              delete.
            </p>{" "}
            <ul className="text-sm">
              {" "}
              {boardsList.map((board) => (
                <li
                  key={board.id}
                  className="flex justify-between items-center p-1.5 hover:bg-slate-500 rounded-md group"
                >
                  {" "}
                  <span
                    className={`cursor-pointer truncate flex-1 ${board.id === currentBoardId
                      ? "font-bold text-sky-300"
                      : ""
                      }`}
                    onClick={() => handleLoadBoard(board.id)}
                    title={`Load "${board.title}"`}
                  >
                    {" "}
                    {board.title || "Untitled"}{" "}
                  </span>{" "}
                  <button
                    onClick={(e) => openDeleteConfirm(board.id, board.title, e)}
                    className="ml-2 p-1 text-red-500 hover:text-red-300 opacity-50 group-hover:opacity-100 transition-opacity"
                    title={`Delete "${board.title}"`}
                    disabled={isDeleting}
                  >
                    {" "}
                    <Trash2 size={16} />{" "}
                  </button>{" "}
                </li>
              ))}{" "}
            </ul>{" "}
          </div>
        )}
      </div>

      <div className="bg-slate-50 text-slate-700 p-3 sm:p-5 rounded-xl shadow-xl mb-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 mb-3">
          <button
            onClick={handleAddImageClick}
            className={`${buttonBaseClass} bg-sky-500 hover:bg-sky-600 text-white focus:ring-sky-400`}
          >
            <PlusSquare size={18} />
            <span>Add Image(s)</span>
          </button>
          <button
            onClick={handleAddText}
            className={`${buttonBaseClass} bg-green-500 hover:bg-green-600 text-white focus:ring-green-400`}
          >
            <Type size={18} />
            <span>Add Text</span>
          </button>
          <button
            onClick={handleSaveBoard}
            disabled={isSaving || !user}
            className={`${buttonBaseClass} bg-purple-500 hover:bg-purple-600 text-white focus:ring-purple-400`}
          >
            {" "}
            {isSaving ? (
              <Loader className="animate-spin" size={18} />
            ) : (
              <Save size={18} />
            )}{" "}
            <span>
              {isSaving
                ? "Saving..."
                : currentBoardId
                  ? "Update Board"
                  : "Save Board"}
            </span>{" "}
          </button>
          <button
            onClick={handleClearBoard}
            className={`${buttonBaseClass} bg-orange-500 hover:bg-orange-600 text-white focus:ring-orange-400`}
          >
            <X size={18} />
            <span>Clear Board</span>
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 items-end">
          <div>
            <label
              htmlFor="exportSizeSelect"
              className="block text-sm font-medium text-slate-600 mb-1"
            >
              Export Size:
            </label>
            <select
              id="exportSizeSelect"
              value={exportSize}
              onChange={(e) => setExportSize(e.target.value)}
              className={`${editSelectClass}`}
            >
              {Object.keys(EXPORT_SIZES).map((key) => (
                <option key={key} value={key}>
                  {EXPORT_SIZES[key].name} (
                  {key !== "Source" ? key : "Board Size"})
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={handleExportBoard}
            className={`${buttonBaseClass} bg-teal-500 hover:bg-teal-600 text-white focus:ring-teal-400 w-full`}
          >
            <Download size={18} />
            <span>Export Board</span>
          </button>
        </div>
        <p className="text-xs text-slate-500 text-center mb-3">
          {!user && "Sign in to save to the cloud."}
        </p>

        <div>
          {" "}
          <button
            onClick={() => setShowBgSettings(!showBgSettings)}
            className="w-full flex justify-between items-center p-2.5 text-sm font-medium text-slate-700 bg-slate-200 hover:bg-slate-300 rounded-md transition"
          >
            <span>Board Background Settings</span>
            <ChevronDown
              size={20}
              className={`transform transition-transform duration-200 ${showBgSettings ? "rotate-180" : ""
                }`}
            />
          </button>{" "}
          {showBgSettings && (
            <div className="mt-3 p-3 bg-slate-100 rounded-md grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label
                  htmlFor="boardBgColor"
                  className="block text-sm font-medium text-slate-600 mb-1"
                >
                  Background Color:
                </label>
                <input
                  type="color"
                  id="boardBgColor"
                  value={boardBgColor}
                  onChange={(e) => setBoardBgColor(e.target.value)}
                  className="w-full h-10 p-1 border border-slate-400 rounded-md cursor-pointer"
                />
              </div>
              <div className="flex items-center col-span-1 sm:col-span-2 md:col-span-1">
                <input
                  type="checkbox"
                  id="showGrid"
                  checked={showGrid}
                  onChange={(e) => setShowGrid(e.target.checked)}
                  className="h-4 w-4 text-sky-600 border-slate-400 rounded focus:ring-sky-500 mr-2"
                />
                <label htmlFor="showGrid" className="text-sm text-slate-600">
                  Show Grid
                </label>
              </div>
              {showGrid && (
                <>
                  <div>
                    <label
                      htmlFor="gridColor"
                      className="block text-sm font-medium text-slate-600 mb-1"
                    >
                      Grid Color:
                    </label>
                    <input
                      type="color"
                      id="gridColor"
                      value={
                        gridColor.startsWith("rgba") ? "#c8c8c8" : gridColor
                      }
                      onChange={(e) => setGridColor(e.target.value)}
                      className="w-full h-10 p-1 border border-slate-400 rounded-md cursor-pointer"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="gridSize"
                      className="block text-sm font-medium text-slate-600 mb-1"
                    >
                      Grid Size: {gridSize}px
                    </label>
                    <input
                      type="range"
                      id="gridSize"
                      min="10"
                      max="100"
                      step="5"
                      value={gridSize}
                      onChange={(e) => setGridSize(Number(e.target.value))}
                      className={editRangeClass}
                    />
                  </div>
                </>
              )}
            </div>
          )}{" "}
        </div>
        <div className="mt-4">
          {" "}
          <button
            onClick={() => setShowImageSearch(!showImageSearch)}
            className="w-full flex justify-between items-center p-2.5 text-sm font-medium text-slate-700 bg-slate-200 hover:bg-slate-300 rounded-md transition"
          >
            {" "}
            <span>Search & Add Images from Web</span>{" "}
            <ChevronDown
              size={20}
              className={`transform transition-transform duration-200 ${showImageSearch ? "rotate-180" : ""
                }`}
            />{" "}
          </button>{" "}
          {showImageSearch && (
            <div className="mt-3 p-3 bg-slate-100 rounded-md space-y-3">
              <div className="flex space-x-2">
                <input
                  type="text"
                  placeholder="Enter search query..."
                  value={imageSearchQuery}
                  onChange={(e) => setImageSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleImageSearch()}
                  className={`${editInputClass} flex-grow`}
                  disabled={isSearching}
                />
                <button
                  onClick={handleImageSearch}
                  className={`${buttonBaseClass} bg-sky-500 hover:bg-sky-600 text-white px-4 py-2 text-sm disabled:bg-sky-400`}
                  disabled={isSearching || !imageSearchQuery.trim()}
                >
                  {isSearching ? (
                    "Searching..."
                  ) : (
                    <>
                      <Search size={18} />
                      Search
                    </>
                  )}
                </button>
              </div>
              {isSearching && (
                <p className="text-center text-slate-500 text-sm">
                  Searching Pixabay...
                </p>
              )}
              {imageSearchResults.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-60 overflow-y-auto pr-1">
                  {imageSearchResults.map((url, index) => (
                    <div
                      key={index}
                      className="relative group border border-slate-300 rounded-md overflow-hidden bg-slate-200"
                    >
                      <img
                        src={url}
                        alt={`Search result ${index}`}
                        className="w-full h-24 object-cover"
                      />
                      <button
                        onClick={() => handleAddSearchedImage(url)}
                        className="absolute inset-0 bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs p-1 text-center"
                        title="Add to Board"
                      >
                        Add to Board
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {!isSearching &&
                imageSearchQuery.trim() &&
                imageSearchResults.length === 0 && (
                  <p className="text-center text-slate-500 text-sm">
                    No results found.
                  </p>
                )}
            </div>
          )}{" "}
        </div>

        {selectedItemData && (
          <div className="mt-4">
            <button
              onClick={() => setShowEditPanel(!showEditPanel)}
              className="w-full flex justify-between items-center p-2.5 text-sm font-medium text-slate-700 bg-slate-200 hover:bg-slate-300 rounded-md transition"
            >
              <span className="flex items-center">
                <Edit size={16} className="mr-2" />
                Edit Selected Item ({selectedItemData.type})
              </span>
              <ChevronDown
                size={20}
                className={`transform transition-transform duration-200 ${showEditPanel ? "rotate-180" : ""
                  }`}
              />
            </button>
            {showEditPanel && (
              <div className="mt-3 p-3 bg-slate-100 rounded-md">
                <h3 className="text-md font-semibold text-slate-700 mb-3">
                  Editing:{" "}
                  <span className="font-normal text-gray-600 text-sm">
                    {" "}
                    ID: {selectedItemData.id.split("-")[1]}{" "}
                  </span>{" "}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    {" "}
                    <label htmlFor="itemRotation" className={editLabelClass}>
                      <RotateCcw size={14} className="inline mr-1 mb-0.5" />
                      Rotation: {selectedItemData.rotation || 0}°
                    </label>{" "}
                    <input
                      type="range"
                      id="itemRotation"
                      min="0"
                      max="360"
                      value={selectedItemData.rotation || 0}
                      onChange={(e) =>
                        updateItem(selectedItemId, {
                          rotation: parseInt(e.target.value),
                        })
                      }
                      className={editRangeClass}
                    />{" "}
                  </div>
                  {selectedItemData.type === "image" && (
                    <>
                      {" "}
                      <div>
                        {" "}
                        <label
                          htmlFor="itemBorderWidth"
                          className={editLabelClass}
                        >
                          <Square size={14} className="inline mr-1 mb-0.5" />
                          Border Width: {selectedItemData.borderWidth || 0}px
                        </label>{" "}
                        <input
                          type="range"
                          id="itemBorderWidth"
                          min="0"
                          max="20"
                          value={selectedItemData.borderWidth || 0}
                          onChange={(e) =>
                            updateItem(selectedItemId, {
                              borderWidth: parseInt(e.target.value),
                            })
                          }
                          className={editRangeClass}
                        />{" "}
                      </div>{" "}
                      <div>
                        {" "}
                        <label
                          htmlFor="itemBorderColor"
                          className={editLabelClass}
                        >
                          <Palette size={14} className="inline mr-1 mb-0.5" />
                          Border Color:
                        </label>{" "}
                        <input
                          type="color"
                          id="itemBorderColor"
                          value={selectedItemData.borderColor || "#FFFFFF"}
                          onChange={(e) =>
                            updateItem(selectedItemId, {
                              borderColor: e.target.value,
                            })
                          }
                          className="w-full h-9 p-1 border border-slate-400 rounded-md cursor-pointer"
                        />{" "}
                      </div>{" "}
                      <div>
                        {" "}
                        <label
                          htmlFor="itemBorderRadius"
                          className={editLabelClass}
                        >
                          <CornerLeftUp
                            size={14}
                            className="inline mr-1 mb-0.5"
                          />
                          Corner Roundness: {selectedItemData.borderRadius || 0}
                          px
                        </label>{" "}
                        <input
                          type="range"
                          id="itemBorderRadius"
                          min="0"
                          max={Math.min(
                            selectedItemData.width / 2,
                            selectedItemData.height / 2,
                            50
                          )}
                          value={selectedItemData.borderRadius || 0}
                          onChange={(e) =>
                            updateItem(selectedItemId, {
                              borderRadius: parseInt(e.target.value),
                            })
                          }
                          className={editRangeClass}
                        />{" "}
                      </div>{" "}
                    </>
                  )}
                  {selectedItemData.type === "text" && (
                    <>
                      {" "}
                      <div>
                        {" "}
                        <label
                          htmlFor="textFontFamily"
                          className={editLabelClass}
                        >
                          <TextCursorInput
                            size={14}
                            className="inline mr-1 mb-0.5"
                          />
                          Font Family:
                        </label>{" "}
                        <select
                          id="textFontFamily"
                          value={
                            selectedItemData.fontFamily || FONT_FAMILIES[0]
                          }
                          onChange={(e) =>
                            updateItem(selectedItemId, {
                              fontFamily: e.target.value,
                            })
                          }
                          className={editSelectClass}
                        >
                          {" "}
                          {FONT_FAMILIES.map((font) => (
                            <option key={font} value={font}>
                              {font.split(",")[0]}
                            </option>
                          ))}{" "}
                        </select>{" "}
                      </div>{" "}
                      <div>
                        {" "}
                        <label
                          htmlFor="itemFontSize"
                          className={editLabelClass}
                        >
                          <Baseline size={14} className="inline mr-1 mb-0.5" />
                          Font Size: {selectedItemData.fontSize || 24}px
                        </label>{" "}
                        <input
                          type="range"
                          id="itemFontSize"
                          min="8"
                          max="120"
                          value={selectedItemData.fontSize || 24}
                          onChange={(e) =>
                            updateItem(selectedItemId, {
                              fontSize: parseInt(e.target.value),
                            })
                          }
                          className={editRangeClass}
                        />{" "}
                      </div>{" "}
                      <div>
                        {" "}
                        <label
                          htmlFor="itemTextColor"
                          className={editLabelClass}
                        >
                          <PaletteIcon
                            size={14}
                            className="inline mr-1 mb-0.5"
                          />
                          Text Color:
                        </label>{" "}
                        <input
                          type="color"
                          id="itemTextColor"
                          value={selectedItemData.textColor || "#000000"}
                          onChange={(e) =>
                            updateItem(selectedItemId, {
                              textColor: e.target.value,
                            })
                          }
                          className="w-full h-9 p-1 border border-slate-400 rounded-md cursor-pointer"
                        />{" "}
                      </div>{" "}
                      <div className="flex items-center">
                        {" "}
                        <input
                          type="checkbox"
                          id="textOutline"
                          checked={!!selectedItemData.textOutline}
                          onChange={(e) =>
                            updateItem(selectedItemId, {
                              textOutline: e.target.checked,
                            })
                          }
                          className="h-4 w-4 text-sky-600 border-slate-400 rounded focus:ring-sky-500 mr-2"
                        />{" "}
                        <label
                          htmlFor="textOutline"
                          className="text-sm text-slate-600"
                        >
                          Enable Outline
                        </label>{" "}
                      </div>{" "}
                      {selectedItemData.textOutline && (
                        <>
                          {" "}
                          <div>
                            {" "}
                            <label
                              htmlFor="textOutlineColor"
                              className={editLabelClass}
                            >
                              <Palette
                                size={14}
                                className="inline mr-1 mb-0.5"
                              />
                              Outline Color:
                            </label>{" "}
                            <input
                              type="color"
                              id="textOutlineColor"
                              value={
                                selectedItemData.textOutlineColor || "#FFFFFF"
                              }
                              onChange={(e) =>
                                updateItem(selectedItemId, {
                                  textOutlineColor: e.target.value,
                                })
                              }
                              className="w-full h-9 p-1 border border-slate-400 rounded-md cursor-pointer"
                            />{" "}
                          </div>{" "}
                          <div>
                            {" "}
                            <label
                              htmlFor="textOutlineWidth"
                              className={editLabelClass}
                            >
                              <Layers
                                size={14}
                                className="inline mr-1 mb-0.5"
                              />
                              Outline Width:{" "}
                              {selectedItemData.textOutlineWidth || 1}px
                            </label>{" "}
                            <input
                              type="range"
                              id="textOutlineWidth"
                              min="1"
                              max="10"
                              value={selectedItemData.textOutlineWidth || 1}
                              onChange={(e) =>
                                updateItem(selectedItemId, {
                                  textOutlineWidth: parseInt(e.target.value),
                                })
                              }
                              className={editRangeClass}
                            />{" "}
                          </div>{" "}
                        </>
                      )}{" "}
                      <div className="flex items-center">
                        {" "}
                        <input
                          type="checkbox"
                          id="textShadow"
                          checked={!!selectedItemData.textShadow}
                          onChange={(e) =>
                            updateItem(selectedItemId, {
                              textShadow: e.target.checked,
                            })
                          }
                          className="h-4 w-4 text-sky-600 border-slate-400 rounded focus:ring-sky-500 mr-2"
                        />{" "}
                        <label
                          htmlFor="textShadow"
                          className="text-sm text-slate-600"
                        >
                          <Droplet size={14} className="inline mr-1 mb-0.5" />
                          Enable Shadow
                        </label>{" "}
                      </div>{" "}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex-grow flex flex-col items-center justify-center relative w-full">
        {showTip && !selectedItemId && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-slate-900/90 text-gray-200 text-xs sm:text-sm p-2 px-3 rounded-md flex justify-between items-center z-20 shadow-lg max-w-xs sm:max-w-md">
            <span>Tip: Click an item to edit its properties below.</span>
            <button
              onClick={() => setShowTip(false)}
              className="ml-3 text-gray-400 hover:text-white"
            >
              <X size={16} />
            </button>
          </div>
        )}
        <div
          id="vision-board-area"
          ref={boardRef}
          className="w-full max-w-5xl aspect-[16/9] rounded-lg shadow-2xl relative overflow-hidden cursor-grab select-none border-2 border-slate-600"
          style={boardStyle}
          onClick={handleBoardClick}
          onDragOver={(e) => e.preventDefault()}
        >
          {items.map((item) => (
            <div
              key={item.id}
              className={`absolute group ${selectedItemId === item.id
                ? "ring-2 ring-sky-400 shadow-lg"
                : ""
                } ${draggingItem?.id === item.id || resizingItem?.id === item.id
                  ? "cursor-grabbing"
                  : "cursor-grab"
                }`}
              style={{
                left: `${item.x}px`,
                top: `${item.y}px`,
                width: `${item.width}px`,
                height: `${item.height}px`,
                transform: `rotate(${item.rotation || 0}deg)`,
                zIndex: item.zIndex || 1,
                border:
                  item.type === "image" && (item.borderWidth || 0) > 0
                    ? `${item.borderWidth}px solid ${item.borderColor || "#FFFFFF"
                    }`
                    : "none",
                borderRadius:
                  item.type === "image" ? `${item.borderRadius || 0}px` : "0px",
                boxSizing: "border-box",
              }}
              onMouseDown={(e) => handleItemMouseDown(item.id, e)}
              onDoubleClick={(e) => {
                if (item.type === "text") {
                  e.stopPropagation();
                  handleTextDoubleClick(item);
                }
              }}
            >
              {item.type === "image" && (
                <img
                  src={item.content}
                  alt="vision board item"
                  className="w-full h-full object-contain pointer-events-none select-none"
                  style={{
                    borderRadius:
                      item.type === "image"
                        ? `${Math.max(
                          0,
                          (item.borderRadius || 0) - (item.borderWidth || 0)
                        )}px`
                        : "0px",
                  }}
                  draggable="false"
                />
              )}
              {item.type === "text" &&
                (editingTextItemId === item.id ? (
                  <textarea
                    value={editedText}
                    onChange={handleTextChange}
                    onBlur={handleTextBlur}
                    onKeyDown={handleTextKeyDown}
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                    className="w-full h-full p-1.5 text-black bg-white/90 border-2 border-sky-500 resize-none focus:outline-none text-center flex items-center justify-center"
                    style={{
                      fontFamily: item.fontFamily,
                      fontSize: `${item.fontSize}px`,
                      color: item.textColor,
                    }}
                  />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center text-center break-words p-1.5 pointer-events-none select-none overflow-hidden"
                    style={{
                      fontFamily: item.fontFamily,
                      fontSize: `${item.fontSize}px`,
                      color: item.textColor,
                      textShadow: item.textShadow
                        ? "2px 2px 4px rgba(0,0,0,0.5)"
                        : "none",
                      WebkitTextStroke: item.textOutline
                        ? `${item.textOutlineWidth}px ${item.textOutlineColor}`
                        : "none",
                      paintOrder: "stroke fill",
                    }}
                  >
                    {" "}
                    {item.content}{" "}
                  </div>
                ))}
              {selectedItemId === item.id && (
                <div className="item-controls-for-export absolute -top-2.5 -right-2.5 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-150 flex space-x-1 bg-slate-700/90 p-1 rounded-full shadow-md z-10">
                  {item.type === "text" && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTextDoubleClick(item);
                      }}
                      className="p-1 hover:bg-slate-600 rounded-full text-sky-300 hover:text-sky-200"
                      title="Edit Text"
                    >
                      <Edit3 size={14} />
                    </button>
                  )}{" "}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteItem(item.id);
                    }}
                    className="p-1 hover:bg-slate-600 rounded-full text-red-400 hover:text-red-300"
                    title="Delete Item"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
              {selectedItemId === item.id && (
                <div
                  className="resize-handle-for-export absolute -bottom-1.5 -right-1.5 w-4 h-4 bg-sky-500 border-2 border-white rounded-full cursor-se-resize hover:bg-sky-400 z-20"
                  onMouseDown={(e) =>
                    handleResizeHandleMouseDown(item.id, "br", e)
                  }
                  title="Resize Item"
                ></div>
              )}
            </div>
          ))}
        </div>
      </div>

      <input
        type="file"
        ref={imageInputRef}
        onChange={handleImageUpload}
        accept="image/*"
        multiple
        className="hidden"
      />
    </div>
  );
};

export default VisionBoardPage;
